import React, { useContext, useEffect, useState, useCallback, useRef } from "react";
import PropTypes from "prop-types";
import { Flex } from "@domestika-dev/prudencio/dist/atoms/Layout";
import { LiveSessionContext } from "../../../context/LiveSessionContext";
import { LiveSessionAlert } from "../LiveSessionAlert";
import { insertScriptAsync, removeNode } from "../../../../../../js/utils/functions";
import * as LiveSessionVideoAlertTypes from "../../../constants/liveSessionVideoAlertTypes";
import { LiveSessionVideoAlerts } from "../../../constants/liveSessionVideoAlerts";
import * as UserRoles from "../../../constants/roles";
import { config } from "../../../config";
import { isLowQualityBitrate, isStreamlabsError } from "../../../utils/liveSessionPlayer";

let MediaPlayerPackage = null;

const setupPlayer = (url, token) => {
  if (MediaPlayerPackage && MediaPlayerPackage.isPlayerSupported) {
    const player = MediaPlayerPackage.create();
    player.attachHTMLVideoElement(document.getElementById("ivs-video-player"));
    player.load(`${url}?token=${token}`);
    player.setAutoplay(true);
    player.setVolume(0.5);
    return player;
  }

  return null;
};

const alertInitialState = {
  show: false,
  key: null,
  message: null,
  type: null,
};

export const IVSVideoPlayer = ({ width, height, onReady, onEnded, onStreamError }) => {
  const { isTeacher, streaming } = useContext(LiveSessionContext);

  const [player, setPlayer] = useState();
  const [alert, _setAlert] = useState();

  const alertRef = useRef(alert);
  const setAlert = (data) => {
    alertRef.current = data;
    _setAlert(data);
  };

  const setupAlert = ({ key, show }) => {
    if (show) {
      const { message, type } = LiveSessionVideoAlerts[key];
      setAlert({
        show: true,
        key,
        message,
        type,
      });
    } else if (key === alertRef.current?.key) {
      setAlert(alertInitialState);
    }
  };

  const getIVSPlayerEvents = useCallback(() =>
    [
      {
        userRoles: [UserRoles.TEACHER],
        eventName: MediaPlayerPackage.PlayerEventType.QUALITY_CHANGED,
        callback: (quality) => {
          setupAlert({
            key: LiveSessionVideoAlertTypes.TEACHER_LOW_BITRATE_QUALITY,
            show: isLowQualityBitrate(quality?.bitrate || 0),
          });
        },
      },
      {
        userRoles: [UserRoles.TEACHER],
        eventName: MediaPlayerPackage.PlayerState.READY,
        callback: onReady,
      },
      {
        userRoles: [UserRoles.TEACHER],
        eventName: MediaPlayerPackage.PlayerState.ENDED,
        callback: () => {
          onEnded();
          setupAlert({
            key: LiveSessionVideoAlertTypes.TEACHER_STREAMLABS_ERROR,
            show: true,
          });
        },
      },
      {
        userRoles: [UserRoles.TEACHER],
        eventName: MediaPlayerPackage.PlayerEventType.ERROR,
        callback: (error) => {
          const showErrorAlert = isStreamlabsError(error);
          if (showErrorAlert) {
            onStreamError();
            setupAlert({
              key: LiveSessionVideoAlertTypes.TEACHER_STREAMLABS_ERROR,
              show: true,
            });
          }
        },
      },
    ].filter(
      (ivsEvent) =>
        ivsEvent.userRoles.indexOf(isTeacher ? UserRoles.TEACHER : UserRoles.STUDENT) >= 0
    )
  );

  useEffect(() => {
    let awsScriptNode;
    setAlert(alertInitialState);
    insertScriptAsync({ url: config.AWS_IVS_SCRIPT_URL })
      .then((scriptNode) => {
        awsScriptNode = scriptNode;
        MediaPlayerPackage = window.IVSPlayer;
        setPlayer(setupPlayer(streaming?.playbackUrl, streaming?.playerToken));
      })
      .catch((error) => {
        throw new Error(error);
      });

    return () => {
      if (awsScriptNode) {
        removeNode(awsScriptNode);
      }
    };
  }, []);

  useEffect(() => {
    if (player) {
      const ivsEvents = getIVSPlayerEvents();
      ivsEvents.forEach((ivsEvent) => {
        player.addEventListener(ivsEvent.eventName, ivsEvent.callback);
      });
    }
  }, [player]);

  return (
    <Flex
      justifyContent="center"
      backgroundColor="black"
      height={height}
      width={width}
      position="relative"
    >
      {alert?.show && <LiveSessionAlert type={alert?.type} message={alert?.message} />}
      {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
      <video
        controls
        playsInline
        disablePictureInPicture
        id="ivs-video-player"
        style={{ width: "100%" }}
      />
    </Flex>
  );
};

IVSVideoPlayer.propTypes = {
  width: PropTypes.string.isRequired,
  height: PropTypes.string.isRequired,
  onReady: PropTypes.func.isRequired,
  onEnded: PropTypes.func.isRequired,
  onStreamError: PropTypes.func.isRequired,
};

export default IVSVideoPlayer;
